import { useCallback, useMemo } from 'react';
import {
  CodeBlock,
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
  Flex,
  InlineStack,
  Page,
  PageBody,
  Pagination,
  Panel,
  PanelSection,
  Select,
  TextLabel,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useParams, useRouter } from '@tanstack/react-router';
import { parseAsBoolean, parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { DocumentTitle } from '@/components/page/document-title';
import {
  CollectionDetailHeader,
  CollectionImportingState,
  useCollectionAttributesSelectModal,
} from '@/features/collections';
import { useUserRoleForCurrentWorkspace } from '@/features/roles';
import { useRegisterBreadcrumbs, type BreadcrumbItem } from '@/services/breadcrumbs';
import type { Collection } from '@/services/db/collection/entity';
import { useCollection, useCollectionItems, type CollectionItem } from '@/services/db/collection/queries';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { CollectionItemsTable, type CollectionItemsTableSorting } from './components/collection-items-table';

const PER_PAGE = 50;

const VIEW_AS_OPTIONS = [
  { label: 'Table', value: 'table' },
  { label: 'Raw data', value: 'raw' },
];

function getVisibleColumns(schema: string[], displayedAttributes: string[]): string[] {
  const uniqueSchema = [...new Set(schema)];
  const schemaAttributes = new Set(uniqueSchema);
  const visibleDisplayedAttributes = [...new Set(displayedAttributes)].filter((attribute) =>
    schemaAttributes.has(attribute),
  );

  return visibleDisplayedAttributes.length > 0 ? visibleDisplayedAttributes : uniqueSchema.slice(0, 5);
}

function normalizeViewAs(viewAs: string): 'raw' | 'table' {
  return viewAs === 'raw' ? 'raw' : 'table';
}

function normalizePage(page: number): number {
  return page < 1 ? 1 : page;
}

function useCollectionDataQueryState() {
  const [viewAs, setViewAs] = useQueryState('viewAs', parseAsString.withDefault('table'));
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [filters] = useQueryState('filters', parseAsString);
  const [sort, setSort] = useQueryState('sort', parseAsString);
  const [asc, setAsc] = useQueryState('asc', parseAsBoolean.withDefault(false));

  return {
    asc,
    filters,
    page: normalizePage(page),
    setAsc,
    setPage,
    setSort,
    setViewAs,
    sort,
    viewAs: normalizeViewAs(viewAs),
  };
}

type CollectionDataQueryState = ReturnType<typeof useCollectionDataQueryState>;

type CollectionDataBreadcrumbsProps = {
  collectionId: string;
  collectionName: string;
  workspaceId: number;
};

function CollectionDataBreadcrumbs({ collectionId, collectionName, workspaceId }: CollectionDataBreadcrumbsProps) {
  const router = useRouter();
  const breadcrumbs = useMemo<BreadcrumbItem[]>(
    () => [
      {
        id: 'collections',
        text: 'Collections',
        href: router.buildLocation({ to: '/workspaces/$workspaceId/journeys/collections', params: { workspaceId } })
          .href,
      },
      {
        id: `collection-${collectionId}`,
        text: collectionName,
        href: router.buildLocation({
          to: '/workspaces/$workspaceId/journeys/collections/$collectionId',
          params: { workspaceId, collectionId },
        }).href,
        isTruncated: true,
      },
    ],
    [collectionId, collectionName, router, workspaceId],
  );
  useRegisterBreadcrumbs(breadcrumbs);
  return null;
}

type CollectionDataToolbarProps = {
  page: number;
  total: number;
  viewAs: 'raw' | 'table';
  onPageChange: (nextPage: number) => void;
  onViewAsChange: (value: string | null) => void;
};

function CollectionDataToolbar({ page, total, viewAs, onPageChange, onViewAsChange }: CollectionDataToolbarProps) {
  return (
    <InlineStack alignBlock="center" alignInline="space-between" mb="150">
      <InlineStack alignBlock="center" gap="100">
        <TextLabel id="view-as-label" htmlFor="view-as-select">
          View as
        </TextLabel>
        <Select
          ariaLabelledby="view-as-label"
          id="view-as-select"
          options={VIEW_AS_OPTIONS}
          value={viewAs}
          onChange={onViewAsChange}
          size="sm"
        />
      </InlineStack>
      <Pagination totalItemCount={total} pageSize={PER_PAGE} page={page} onPageChange={onPageChange} />
    </InlineStack>
  );
}

type EmptyCollectionItemsPanelProps = {
  hasFilters: boolean;
};

function EmptyCollectionItemsPanel({ hasFilters }: EmptyCollectionItemsPanelProps) {
  return (
    <Panel my="150">
      <PanelSection>
        <EmptyState>
          <EmptyStateTitle icon="database">
            {hasFilters ? 'No items match your filters' : 'No collection items yet'}
          </EmptyStateTitle>
          <EmptyStateDescription>
            {hasFilters ? 'Try changing your filters.' : 'Items will appear here when this collection has data.'}
          </EmptyStateDescription>
        </EmptyState>
      </PanelSection>
    </Panel>
  );
}

type RawCollectionItemsPanelProps = {
  items: unknown[];
};

function RawCollectionItemsPanel({ items }: RawCollectionItemsPanelProps) {
  return (
    <Panel my="150">
      <PanelSection>
        <CodeBlock language="json" code={JSON.stringify(items, null, 2)} className="fs-mask" />
      </PanelSection>
    </Panel>
  );
}

type CollectionDataContentProps = {
  canEdit: boolean;
  collection: Collection;
  items: CollectionItem[];
  sorting?: CollectionItemsTableSorting;
  hasFilters: boolean;
  total: number;
  viewAs: 'raw' | 'table';
  visibleColumns: string[];
  onShowColumnsModal: () => void;
  onSortingChange: (sorting: CollectionItemsTableSorting) => void;
};

function CollectionDataContent({
  canEdit,
  collection,
  hasFilters,
  items,
  sorting,
  total,
  viewAs,
  visibleColumns,
  onShowColumnsModal,
  onSortingChange,
}: CollectionDataContentProps) {
  if (collection.importing) {
    return <CollectionImportingState />;
  }

  if (items.length === 0 && total === 0) {
    return <EmptyCollectionItemsPanel hasFilters={hasFilters} />;
  }

  if (viewAs === 'raw') {
    return <RawCollectionItemsPanel items={items} />;
  }

  return (
    <CollectionItemsTable
      collection={collection}
      items={items}
      sorting={sorting}
      visibleColumns={visibleColumns}
      canEditDisplayedColumns={canEdit}
      onSortingChange={onSortingChange}
      onShowColumnsModal={onShowColumnsModal}
    />
  );
}

function useCanEditDisplayedAttributes() {
  const { hasPermission } = usePermissions();
  const userRole = useUserRoleForCurrentWorkspace();

  return hasPermission('journeys/collections/edit') === true && userRole?.redacted !== true;
}

type CollectionDataPageHandlersParams = {
  collection: Collection;
  queryState: CollectionDataQueryState;
  workspaceId: number;
};

function useCollectionDataPageHandlers({ collection, queryState, workspaceId }: CollectionDataPageHandlersParams) {
  const router = useRouter();
  const attributesModal = useCollectionAttributesSelectModal();
  const { setAsc, setPage, setSort, setViewAs } = queryState;

  const handleSortingChange = useCallback(
    (next: CollectionItemsTableSorting) => {
      void setSort(next.id);
      void setAsc(!next.desc);
    },
    [setAsc, setSort],
  );
  const handleViewAsChange = (value: string | null) => {
    void setViewAs(value === 'raw' ? 'raw' : 'table');
  };
  const handlePageChange = (nextPage: number) => {
    void setPage(nextPage);
  };
  const showColumnsModal = () => {
    attributesModal.open({ collection, workspaceId });
  };
  const handleCollectionDeleted = useCallback(() => {
    void router.navigate({ to: '/workspaces/$workspaceId/journeys/collections', params: { workspaceId } });
  }, [router, workspaceId]);

  return {
    handleCollectionDeleted,
    handlePageChange,
    handleSortingChange,
    handleViewAsChange,
    showColumnsModal,
  };
}

function useCollectionDataPageModel() {
  const { collectionId } = useParams({ strict: false });
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const canEditDisplayedAttributes = useCanEditDisplayedAttributes();
  const queryState = useCollectionDataQueryState();
  const { asc, filters, page, sort, viewAs } = queryState;
  const { data: collection } = useCollection({ id: collectionId ?? '' });
  const { data } = useCollectionItems({
    workspaceId,
    collectionId: Number(collection.id),
    page,
    limit: PER_PAGE,
    conditions: filters,
    orderBy: sort,
    asc,
  });
  const visibleColumns = useMemo(
    () => getVisibleColumns(collection.schema, collection.displayed_attributes),
    [collection.displayed_attributes, collection.schema],
  );
  const sorting = useMemo<CollectionItemsTableSorting | undefined>(
    () => (sort == null ? undefined : { id: sort, desc: !asc }),
    [asc, sort],
  );
  const handlers = useCollectionDataPageHandlers({ collection, queryState, workspaceId });

  return {
    canEditDisplayedAttributes,
    collection,
    filters,
    handleCollectionDeleted: handlers.handleCollectionDeleted,
    handlePageChange: handlers.handlePageChange,
    handleSortingChange: handlers.handleSortingChange,
    handleViewAsChange: handlers.handleViewAsChange,
    items: data.collection_items,
    page,
    showColumnsModal: handlers.showColumnsModal,
    sorting,
    total: data.total,
    viewAs,
    visibleColumns,
    workspaceId,
  };
}

export default function DataPage() {
  const pageModel = useCollectionDataPageModel();

  return (
    <>
      <DocumentTitle title={`${pageModel.collection.name} data`} />
      <CollectionDataBreadcrumbs
        collectionId={pageModel.collection.id}
        collectionName={pageModel.collection.name}
        workspaceId={pageModel.workspaceId}
      />
      <Page flexGrow="1">
        <CollectionDetailHeader
          collection={pageModel.collection}
          workspaceId={pageModel.workspaceId}
          onDeleted={pageModel.handleCollectionDeleted}
        />
        <PageBody>
          <CollectionDataToolbar
            page={pageModel.page}
            total={pageModel.total}
            viewAs={pageModel.viewAs}
            onPageChange={pageModel.handlePageChange}
            onViewAsChange={pageModel.handleViewAsChange}
          />

          <CollectionDataContent
            canEdit={pageModel.canEditDisplayedAttributes}
            collection={pageModel.collection}
            hasFilters={pageModel.filters != null}
            items={pageModel.items}
            sorting={pageModel.sorting}
            total={pageModel.total}
            viewAs={pageModel.viewAs}
            visibleColumns={pageModel.visibleColumns}
            onShowColumnsModal={pageModel.showColumnsModal}
            onSortingChange={pageModel.handleSortingChange}
          />

          <Flex justifyContent="flex-end">
            <Pagination
              totalItemCount={pageModel.total}
              pageSize={PER_PAGE}
              page={pageModel.page}
              onPageChange={pageModel.handlePageChange}
            />
          </Flex>
        </PageBody>
      </Page>
    </>
  );
}
